import {
  convertQuizQuestionToQuestionDataType,
  sanitizeHTML,
  stripHTML,
  UpdateEntityPositions,
} from '@/lib/util';
import {
  useCreateQuestionMutation,
  useGetElearningQuery,
  useGetQuestionsQuery,
  useGetQuizQuery,
  useGetThemesQuery,
  useRepositionQuestionMutation,
  useUpdateQuizMutation,
} from '@/modules/services';
import {
  AddIcon,
  Badge,
  Button,
  Col,
  DragIndicatorIcon,
  Paragraph,
  Row,
  Text,
  Popover,
} from 'elmo-elements';
import React, { ReactElement, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { useErrorHandler } from 'react-error-boundary';
import {
  useParams,
  Link,
  useLocation,
  generatePath,
  useHistory,
} from 'react-router-dom';
import QuizQuestionsRoutes, { QuestionParamType } from '../Questions/routes';
import QuizFeedbackRoutes from '../ResultFeedback/routes';
import QuizRoutes from '../routes';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';
import { defaultQuizValues } from './quizSetup';

export const QuizSideBar = (): ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const handleError = useErrorHandler();
  const { elearningId, questionId } = useParams<QuestionParamType>();
  const [currentPath, setCurrentPath] = useState<string>('');
  const quiz = useGetQuizQuery(elearningId);
  const elearning = useGetElearningQuery(elearningId);
  const [updateQuiz] = useUpdateQuizMutation();
  const [createQuestion, createQuestionStatus] = useCreateQuestionMutation();
  const [repositionQuestion, repositionQuestionStatus] =
    useRepositionQuestionMutation();
  const { data: questionsData, isFetching: isFetchingQuestion } =
    useGetQuestionsQuery(
      {
        quizId: `${quiz.data?.id}`,
      },
      {
        skip: !quiz.data,
      },
    );

  const themes = useGetThemesQuery();

  useEffect(() => {
    if (!createQuestionStatus.isError && !createQuestionStatus.isSuccess)
      return;

    let message = 'Create new question error! Something went wrong.';
    if (createQuestionStatus.isSuccess) {
      message = 'Create new question successful.';
    }

    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });

    if (createQuestionStatus.isLoading || !createQuestionStatus.data) return;

    // Redirect to newly created question
    history.push(
      generatePath(QuizQuestionsRoutes.path, {
        elearningId,
        questionId: createQuestionStatus.data.id,
      }),
    );
  }, [createQuestionStatus]);

  useEffect(() => {
    const path = location.pathname
      .split('/')
      .filter((splitPath) => splitPath && splitPath.trim());

    if (path.length > 0) setCurrentPath(path[path.length - 1]);
  }, [location]);

  useEffect(() => {
    const focusedQuestionId = questionId || '';
    const dndQuestionRef = document.querySelector(
      `[data-rbd-draggable-id="${focusedQuestionId}"]`,
    );

    if (dndQuestionRef) {
      dndQuestionRef.scrollIntoView();
    }
  }, [questionId, questionsData]);

  if (quiz.isError) {
    handleError(quiz.error);
  }

  if (themes.isError) {
    handleError(themes.error);
  }

  const handleCreateQuiz = () => {
    if (
      quiz.data &&
      elearning.data &&
      (!quiz.data.quizMetadata ||
        Object.keys(quiz.data?.quizMetadata).length <= 0)
    ) {
      const submitData = {
        ...quiz.data,
        name: `${elearning.data?.name} Quiz`,
        quizMetadata: {
          ...quiz.data?.quizMetadata,
          questionNo: 1,
          compulsoryNo: defaultQuizValues.compulsoryNo,
          pass: defaultQuizValues.passMark,
          showCorrect: defaultQuizValues.showAnswer,
          showAnswerFeedback: defaultQuizValues.correctFeedback,
          lmsTrackingEnable: defaultQuizValues.responseTracking,
        },
        scormFeedbackMetadata: {
          ...quiz.data.scormFeedbackMetadata,
          hideRedoBtn: defaultQuizValues.scormHideRedo,
        },
      };
      updateQuiz(submitData);
    }
  };

  const handleCreateQuestion = (index: number) => {
    handleCreateQuiz();

    const submitData = {
      id: quiz.data?.id || '',
      content: convertQuizQuestionToQuestionDataType({
        question: `Question ${index + 1}`,
        compulsory: 0,
        disabled: 0,
        responseType: 3,
        position: index,
        correctfeedback: '',
        incorrectfeedback: '',
      }),
    };
    createQuestion(submitData);
  };

  const UpdatePositions = (sourceIndex: number, destinationIndex: number) => {
    if (!questionsData) return;

    const allRepositionQuestions = UpdateEntityPositions(
      questionsData,
      sourceIndex,
      destinationIndex,
    );

    if (quiz.data) {
      repositionQuestion({
        quizId: quiz.data?.id,
        questionPositions: allRepositionQuestions,
      });
    }
  };

  const handleOnDragEnd = (dropResult: DropResult) => {
    if (!dropResult.destination) return;

    UpdatePositions(dropResult.source.index, dropResult.destination.index);
  };

  return (
    <div className="editor-layout__nav">
      <nav className="editor-nav">
        <Link
          className={currentPath === 'quiz' ? 'is-active' : ''}
          to={generatePath(QuizRoutes.path, { elearningId })}
        >
          Setup
        </Link>
        <Link
          className={currentPath === 'feedback' ? 'is-active' : ''}
          to={generatePath(QuizFeedbackRoutes.path, { elearningId })}
        >
          Feedback
        </Link>
      </nav>
      <div className="frame-nav">
        <Row
          isNoGutters={true}
          alignItems="center"
          className="py-3 frame-nav__header"
        >
          <Col span={12} className="pl-3">
            <Link
              to={generatePath(QuizQuestionsRoutes.path, {
                elearningId,
                questionId:
                  questionsData && questionsData.length > 0
                    ? questionsData[0].id
                    : 'questions',
              })}
              className="btn-slides"
            >
              Questions
              {!!questionsData && (
                <Badge type="outlined">{questionsData.length}</Badge>
              )}
            </Link>
          </Col>
          <Col span={12} className="add-icon">
            <Button
              onClick={() =>
                handleCreateQuestion(questionsData ? questionsData.length : 0)
              }
              className="color-b"
              isUppercase={false}
              isLoading={createQuestionStatus.isLoading || isFetchingQuestion}
            >
              <AddIcon size="md" />
              <Text>Add</Text>
            </Button>
          </Col>
        </Row>

        <nav className="quiz-nav-list">
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="droppable-draggroup">
              {(providedDrop) => (
                <div
                  className="question-group"
                  {...providedDrop.droppableProps}
                  ref={providedDrop.innerRef}
                >
                  {!!questionsData &&
                    [...questionsData]
                      .sort((a, b) =>
                        a.questionMetadata?.position &&
                        b.questionMetadata?.position
                          ? a.questionMetadata.position -
                            b.questionMetadata.position
                          : 0,
                      )
                      .map((questionData, index) => {
                        const purifiedQuestionTitle = (questionData.content &&
                          (sanitizeHTML(
                            questionData.content,
                          ) as unknown)) as string;
                        return (
                          <Draggable
                            key={questionData.content + index + questionData.id}
                            index={index}
                            draggableId={`${questionData.id}`}
                            isDragDisabled={
                              repositionQuestionStatus.isLoading ||
                              isFetchingQuestion
                            }
                          >
                            {(providedDrag) => (
                              <div
                                className={`${
                                  repositionQuestionStatus.isLoading ||
                                  isFetchingQuestion
                                    ? 'cursor-wait'
                                    : ''
                                }`}
                                data-testid="draggable-question"
                                ref={providedDrag.innerRef}
                                {...providedDrag.dragHandleProps}
                                {...providedDrag.draggableProps}
                              >
                                <Row
                                  role="row"
                                  isNoGutters={true}
                                  alignItems="center"
                                  wrap="nowrap"
                                  className={`slideItem-row ${
                                    currentPath === questionData.id
                                      ? 'is-active'
                                      : ''
                                  }`}
                                >
                                  <Col
                                    align="center"
                                    role="cell"
                                    isAuto={true}
                                    className="align-center question-item"
                                  >
                                    <div className="slide-body">
                                      <DragIndicatorIcon className="q-drag-icon" />
                                      <Popover
                                        className="quiz-popover"
                                        isAutoWidth={true}
                                        isNoPadding={true}
                                        position="right"
                                      >
                                        <Popover.Target>
                                          <Paragraph
                                            color="gray"
                                            size="sm"
                                            className="padding-question"
                                          >
                                            <Link
                                              className="question-menu-item"
                                              to={generatePath(
                                                QuizQuestionsRoutes.path,
                                                {
                                                  elearningId,
                                                  questionId: questionData.id,
                                                },
                                              )}
                                            >
                                              <span className="question-menu-item-description">
                                                {`${index + 1}. ${stripHTML(
                                                  questionData.content,
                                                )}`}
                                              </span>
                                              {(+questionData.questionMetadata
                                                .compulsory > 0 ||
                                                +questionData.questionMetadata
                                                  .disabled > 0) && (
                                                <Text
                                                  size="xs"
                                                  className="indicator"
                                                >
                                                  {+questionData
                                                    .questionMetadata
                                                    .compulsory > 0 &&
                                                    +questionData
                                                      .questionMetadata
                                                      .disabled === 0 && (
                                                      <span className="mandatory">
                                                        Mandatory
                                                      </span>
                                                    )}
                                                  {+questionData
                                                    .questionMetadata.disabled >
                                                    0 && (
                                                    <span className="disabled">
                                                      Disabled
                                                    </span>
                                                  )}
                                                </Text>
                                              )}
                                            </Link>
                                          </Paragraph>
                                        </Popover.Target>
                                        <Popover.Content>
                                          <div
                                            // eslint-disable-next-line jam3/no-sanitizer-with-danger, react/no-danger
                                            dangerouslySetInnerHTML={{
                                              __html: purifiedQuestionTitle,
                                            }}
                                            className="question-title-tooltip"
                                          />
                                        </Popover.Content>
                                      </Popover>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                  {providedDrop.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </nav>
      </div>
    </div>
  );
};

export default QuizSideBar;
